import React, { useState, useContext, useEffect, useRef } from 'react';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { getPhoneConfig, validatePhone } from '../../utils/phoneUtils';
import { safePhoneE164 } from '../../utils/sanitize';

export function StatCard({ label, value, icon: Icon, symbol, color = 'blue', sub }) {
  const colors = {
    blue:   'bg-blue-50 text-blue-600',
    green:  'bg-green-50 text-green-600',
    orange: 'bg-orange-50 text-orange-600',
    red:    'bg-red-50 text-red-600',
    purple: 'bg-purple-50 text-purple-600',
  };
  return (
    <div className="card flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${colors[color]}`}>
        {symbol
          ? <span className="text-xl font-bold leading-none">{symbol}</span>
          : Icon && <Icon size={24} />}
      </div>
      <div>
        <p className="text-2xl font-bold text-neutral-800">{value ?? '—'}</p>
        <p className="text-xs text-neutral-500 font-medium">{label}</p>
        {sub && <p className="text-xs text-neutral-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

export function Spinner({ size = 'md' }) {
  const s = { sm: 'w-4 h-4', md: 'w-8 h-8', lg: 'w-12 h-12' }[size];
  return (
    <div className={`${s} border-2 border-neutral-200 border-t-primary rounded-full animate-spin`} />
  );
}

export function LoadingPage() {
  return (
    <div className="flex items-center justify-center h-64">
      <Spinner size="lg" />
    </div>
  );
}

export function EmptyState({ title, description, action }) {
  // action can be a React element OR a { label, onClick } shorthand object
  const actionNode = action && typeof action === 'object' && !React.isValidElement(action)
    ? <button onClick={action.onClick} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">{action.label}</button>
    : action;
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-16 h-16 rounded-full bg-neutral-100 flex items-center justify-center mb-4">
        <span className="text-3xl">🔍</span>
      </div>
      <h3 className="font-semibold text-neutral-700">{title}</h3>
      {description && <p className="text-sm text-neutral-400 mt-1 max-w-xs">{description}</p>}
      {actionNode && <div className="mt-4">{actionNode}</div>}
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    // Workforce
    ACTIVE:     'badge-green',
    INACTIVE:   'badge-gray',
    // Appointment
    SCHEDULED:  'badge-blue',
    CONFIRMED:  'badge-green',
    COMPLETED:  'badge-green',
    CANCELLED:  'badge-red',
    NO_SHOW:    'badge-gray',
    // Factory / Equipment
    PENDING:    'badge-yellow',
    IN_PROGRESS:'badge-blue',
    ON_HOLD:    'badge-gray',
    RETURNED:   'badge-gray',
    MAINTENANCE:'badge-yellow',
    // Billing
    PAID:       'badge-green',
    PARTIAL:    'badge-orange',
    OVERDUE:    'badge-red',
    REFUNDED:   'badge-gray',
  };
  return <span className={map[status] || 'badge-gray'}>{status?.replace('_', ' ')}</span>;
}

export function Pagination({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-2 mt-4">
      <button
        disabled={page === 0}
        onClick={() => onPageChange(page - 1)}
        className="btn-secondary text-xs px-3 py-1.5"
      >
        Previous
      </button>
      <span className="text-sm text-neutral-500">
        Page {page + 1} of {totalPages}
      </span>
      <button
        disabled={page >= totalPages - 1}
        onClick={() => onPageChange(page + 1)}
        className="btn-secondary text-xs px-3 py-1.5"
      >
        Next
      </button>
    </div>
  );
}

const ModalCloseContext = React.createContext(() => {});

export function useModalClose() {
  return useContext(ModalCloseContext);
}

export function Modal({ open = true, onClose, title, children, footer, wide, confirmClose = false }) {
  const [askConfirm, setAskConfirm] = useState(false);

  if (!open) return null;

  const handleCloseRequest = () => {
    if (confirmClose) {
      setAskConfirm(true);
    } else {
      onClose();
    }
  };

  return (
    <ModalCloseContext.Provider value={handleCloseRequest}>
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" />
      <div className={`relative bg-white rounded-2xl shadow-2xl w-full max-h-[90vh]
                      flex flex-col overflow-hidden ${wide ? 'max-w-4xl' : 'max-w-lg'}`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <h2 className="text-base font-semibold">{title}</h2>
          <button onClick={handleCloseRequest}
            className="p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-400 hover:text-neutral-700">
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
        {footer && (
          <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-3">
            {footer}
          </div>
        )}

        {/* Inline close-confirmation overlay */}
        {askConfirm && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/30 rounded-2xl">
            <div className="bg-white rounded-xl shadow-xl p-6 mx-4 w-full max-w-sm text-center">
              <div className="w-12 h-12 rounded-full bg-yellow-50 flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">⚠️</span>
              </div>
              <h3 className="text-base font-semibold text-neutral-800 mb-1">Close without saving?</h3>
              <p className="text-sm text-neutral-500 mb-5">
                Any unsaved changes will be lost. Are you sure you want to close?
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setAskConfirm(false)}
                  className="px-5 py-2 rounded-lg border border-neutral-300 text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition-colors"
                >
                  No, Keep Editing
                </button>
                <button
                  onClick={() => { setAskConfirm(false); onClose(); }}
                  className="px-5 py-2 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors"
                >
                  Yes, Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </ModalCloseContext.Provider>
  );
}

export function ModalCancelButton({ children = 'Cancel', className = 'btn-secondary', type = 'button' }) {
  const requestClose = useContext(ModalCloseContext);
  return (
    <button type={type} className={className} onClick={requestClose}>
      {children}
    </button>
  );
}

export function FormField({ label, error, children }) {
  return (
    <div className="space-y-1">
      {label && <label className="label">{label}</label>}
      {children}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

export function AlertBanner({ type = 'error', message, onDismiss, onClose }) {
  if (!message) return null;
  const styles = {
    error:   'bg-red-50 border-red-200 text-red-700',
    success: 'bg-green-50 border-green-200 text-green-700',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    info:    'bg-blue-50 border-blue-200 text-blue-700',
  };
  return (
    <div className={`flex items-center justify-between px-4 py-2.5 rounded-lg border text-sm ${styles[type]}`}>
      <span>{message}</span>
      {(onDismiss || onClose) && (
        <button onClick={onDismiss || onClose} className="ml-3 font-bold opacity-60 hover:opacity-100">✕</button>
      )}
    </div>
  );
}

export function Button({ children, variant = 'primary', type = 'button', disabled, onClick, className = '' }) {
  const base = 'inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed';
  const variants = {
    primary:   'bg-primary text-white hover:bg-primary/90',
    secondary: 'bg-neutral-100 text-neutral-700 border border-neutral-200 hover:bg-neutral-200',
    danger:    'bg-red-500 text-white hover:bg-red-600',
  };
  return (
    <button type={type} disabled={disabled} onClick={onClick}
      className={`${base} ${variants[variant] || variants.primary} ${className}`}>
      {children}
    </button>
  );
}

export function Badge({ children, color = 'gray' }) {
  const colors = {
    green:  'bg-green-100 text-green-700',
    red:    'bg-red-100 text-red-700',
    orange: 'bg-orange-100 text-orange-700',
    blue:   'bg-blue-100 text-blue-700',
    purple: 'bg-purple-100 text-purple-700',
    yellow: 'bg-yellow-100 text-yellow-700',
    gray:   'bg-neutral-100 text-neutral-600',
  };
  return (
    <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${colors[color] || colors.gray}`}>
      {children}
    </span>
  );
}

export function LoadingSpinner() {
  return <Spinner size="lg" />;
}

/**
 * PhoneInput — a phone number input with inline currency-aware validation.
 *
 * Props:
 *   label      – field label text (optional)
 *   value      – controlled value
 *   onChange   – called with the new string value
 *   currency   – ISO 4217 code from tenant settings (e.g. "INR", "USD")
 *   required   – when true, shows * and validates on blur even if empty
 *   className  – extra classes for the <input>
 *   disabled
 */
export function PhoneInput({ label, value, onChange, currency, required = false, className = '', disabled = false }) {
  const [error, setError] = useState('');
  const config = getPhoneConfig(currency);

  const handleBlur = () => {
    if (!value && !required) { setError(''); return; }
    if (!value && required)  { setError('Phone number is required'); return; }
    const err = validatePhone(value, currency);
    setError(err || '');
  };

  const handleChange = (e) => {
    onChange(e.target.value);
    if (error) setError('');
  };

  return (
    <div>
      {label && (
        <label className="label">
          {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <input
        type="tel"
        className={`input ${error ? 'border-red-400 focus:ring-red-300' : ''} ${className}`}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={config.placeholder}
        disabled={disabled}
      />
      {error
        ? <p className="text-xs text-red-500 mt-0.5">{error}</p>
        : <p className="text-xs text-neutral-400 mt-0.5">{config.hint}</p>
      }
    </div>
  );
}

// ── WhatsApp Send Modal ─────────────────────────────────────────────────────
// Allows user to confirm or override the recipient phone number before opening WhatsApp.
export function WhatsAppSendModal({ open, onClose, defaultPhone = '', message = '', onSend }) {
  const [phone,         setPhone]         = useState('');
  const [phoneError,    setPhoneError]    = useState('');
  const [editedMessage, setEditedMessage] = useState('');

  useEffect(() => {
    if (open) { setPhone(defaultPhone); setPhoneError(''); setEditedMessage(message); }
  }, [open, defaultPhone, message]);

  const handleSend = () => {
    const e164 = safePhoneE164(phone.trim());
    if (!e164) { setPhoneError('Enter a valid phone number (digits only, min 5)'); return; }
    window.open(`https://wa.me/${e164}?text=${encodeURIComponent(editedMessage)}`, '_blank', 'noopener,noreferrer');
    onSend && onSend();
    onClose();
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100">
          <div className="flex items-center gap-2">
            <span className="text-emerald-600 text-xl">&#128731;</span>
            <h2 className="font-semibold text-neutral-800 text-base">Send WhatsApp Message</h2>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700 p-1 rounded">
            &#10005;
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="label">Send to phone number</label>
            <input
              type="tel"
              autoFocus
              className={`input ${phoneError ? 'border-red-400 focus:ring-red-300' : ''}`}
              value={phone}
              onChange={e => { setPhone(e.target.value); setPhoneError(''); }}
              placeholder="e.g. 9876543210 or +919876543210"
            />
            {phoneError
              ? <p className="text-xs text-red-500 mt-0.5">{phoneError}</p>
              : <p className="text-xs text-neutral-400 mt-0.5">Pre-filled from customer record. You can change it to any number.</p>
            }
          </div>
          <div>
            <label className="label">Message <span className="text-neutral-400 font-normal">(edit before sending)</span></label>
            <textarea
              rows={6}
              className="input resize-none text-xs text-neutral-700"
              value={editedMessage}
              onChange={e => setEditedMessage(e.target.value)}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 pb-4">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button
            onClick={handleSend}
            className="btn-primary text-sm flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-400">
            <span>&#128731;</span> Open WhatsApp
          </button>
        </div>
      </div>
    </div>
  );
}

// ── MonthYearPicker ──────────────────────────────────────────────────────────
// Click the month/year label to open a popover with year arrows + 12 month grid.
// Props: year (number), month (1-12), onChange(year, month)
export function MonthYearPicker({ year, month, onChange }) {
  const [open, setOpen]           = useState(false);
  const [pickerYear, setPickerYear] = useState(year);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => { setPickerYear(year); }, [year]);

  const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  const prev = () => onChange(month === 1 ? year - 1 : year, month === 1 ? 12 : month - 1);
  const next = () => onChange(month === 12 ? year + 1 : year, month === 12 ? 1 : month + 1);

  return (
    <div className="relative flex items-center gap-1" ref={ref}>
      <button onClick={prev} className="p-1.5 hover:bg-gray-100 rounded" title="Previous month">
        <MdChevronLeft size={20} />
      </button>

      <button
        onClick={() => { setPickerYear(year); setOpen(o => !o); }}
        className="px-3 py-1.5 font-semibold text-base min-w-36 text-center rounded-lg hover:bg-gray-100 border border-transparent hover:border-gray-200 transition-colors"
      >
        {MONTH_NAMES[month - 1]} {year} <span className="text-xs text-gray-400">▾</span>
      </button>

      <button onClick={next} className="p-1.5 hover:bg-gray-100 rounded" title="Next month">
        <MdChevronRight size={20} />
      </button>

      {open && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-xl p-3 w-60">
          {/* Year row */}
          <div className="flex items-center justify-between mb-3 px-1">
            <button onClick={() => setPickerYear(y => y - 1)} className="p-1 hover:bg-gray-100 rounded">
              <MdChevronLeft size={18} />
            </button>
            <span className="font-bold text-gray-800 text-base">{pickerYear}</span>
            <button onClick={() => setPickerYear(y => y + 1)} className="p-1 hover:bg-gray-100 rounded">
              <MdChevronRight size={18} />
            </button>
          </div>
          {/* Month grid */}
          <div className="grid grid-cols-4 gap-1">
            {MONTH_NAMES.map((name, i) => {
              const isSelected = pickerYear === year && i + 1 === month;
              return (
                <button
                  key={name}
                  onClick={() => { onChange(pickerYear, i + 1); setOpen(false); }}
                  className={`py-1.5 text-sm rounded-lg font-medium transition-colors ${
                    isSelected
                      ? 'bg-blue-600 text-white'
                      : 'hover:bg-blue-50 text-gray-700'
                  }`}
                >
                  {name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
